import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { applyTheme } from "@/lib/theme";

const AdminContentContext = createContext(null);

export function AdminContentProvider({ children }) {
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState({ published_at: null });
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/content");
    setDraft(data.draft);
    applyTheme(data.draft?.theme);
    setMeta({ published_at: data.published_at });
    setDirty(false);
  }, []);

  useEffect(() => { load().catch(() => toast.error("Não foi possível carregar o conteúdo.")); }, [load]);

  const update = useCallback((section, patch) => {
    setDraft((d) => {
      const next = { ...d, [section]: typeof patch === "function" ? patch(d[section]) : { ...d[section], ...patch } };
      if (section === "theme") applyTheme(next.theme);
      return next;
    });
    setDirty(true);
  }, []);

  const save = useCallback(async (silent = false, override = null) => {
    const payload = override || draft;
    if (!payload) return false;
    setBusy(true);
    try {
      const { data } = await api.put("/admin/content", payload);
      if (override) setDraft(override);
      setMeta({ published_at: data.published_at });
      setDirty(false);
      if (!silent) toast.success("Guardado. O site já mostra as alterações.");
      return true;
    } catch (e) {
      toast.error(errorMessage(e, "Não foi possível guardar."));
      return false;
    } finally {
      setBusy(false);
    }
  }, [draft]);

  const value = useMemo(() => ({ draft, meta, dirty, busy, update, save, reload: load }), [draft, meta, dirty, busy, update, save, load]);

  return <AdminContentContext.Provider value={value}>{children}</AdminContentContext.Provider>;
}

export const useAdminContent = () => useContext(AdminContentContext);
